import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, usePopup } from "@/comn/hooks";
import { URLS, APIS, SF_INTF_DOC_MGMT_INFO, SG_INTF_ORG_ENPO_LIST } from "./services/OsciIntfDocMgmtService";
import { OSCI0113007Q } from "../enpo/OSCI0113007Q";

/*
 * Insert Document Box
 * !== 문서함 등록 ==!
 */
export const OSCI0113009S = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0164" }, // 내외부 유형 코드
            { area: "comnCd", comnCd: "OSC0088" }, // 업무 분류 코드
            { area: "comnCd", comnCd: "OSC0101" }, // 사용 여부 코드
            { area: "comnCd", comnCd: "OSC0103" }, // 대상 유형 코드
            { area: "comnCd", comnCd: "OSC0104" }, // 송수신 유형 코드
            { area: "comnCd", comnCd: "OSC0105" }, // 연계 프로토콜 코드
            { area: "comnCd", comnCd: "OSC0106" }, // 연계 데이터 유형 코드
            { area: "comnCd", comnCd: "OSC0107" }, // 연계 처리 방법 코드
            { area: "comnCd", comnCd: "OSC0108" }, // 연계 주기 코드
            { area: "comnCd", comnCd: "OSC0109" }, // 송수신 구분 코드
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        intfDocMgmtInfo: useForm({
            defaultSchema: SF_INTF_DOC_MGMT_INFO,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        intfOrgEnpoList: useGrid({
            defaultSchema: SG_INTF_ORG_ENPO_LIST,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Create Document Box !== 문서함 등록 ==!
        insertIntfDoc: useFetch({
            api: (data) => APIS.insertIntfDoc({ ...data }),
            onSuccess: (res) => {
                modal.openModal({
                    content: "msg.osc.00073", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        const intfId = res.intfDocInfo.content.intfId;

                        navigate(`${URLS.osci0113010s}/${intfId}`);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.intfDocMgmtInfo.setErrors(error.response.data.errors);
                }
                const { errorCode, errorMessage } = error.response.data;

                // BizException
                if (errorCode === "-999") {
                    modal.openModal({ content: errorMessage });
                } else {
                    toast.showToast({ type: "error", content: "msg.osc.00035" }); // An error occurred. !== 에러가 발생했습니다! ==!
                }
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // on Save Button Click Event Handler  !== 문서함 등록 ==!
        onSaveBtnClick: () => {
            if (!comnUtils.isEmpty(form.intfDocMgmtInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            form.intfDocMgmtInfo.handleSubmit(
                () => {
                    const data = form.intfDocMgmtInfo.getValues();
                    const intfOrgEnpoLst = grid.intfOrgEnpoList.getViewData();

                    // Setting Document Target List !== 문서 대상 목록 세팅 ==!
                    data.intfOrgEnpoLst = intfOrgEnpoLst;

                    modal.openModal({
                        content: "msg.osc.00090", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.insertIntfDoc.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        onBtnGrdAddRowClick: () => {
            const modalCallback = (list: any) => {
                console.log(list);
                const intfOrgEnpoList = grid.intfOrgEnpoList.getData();
                list.forEach(
                    (item: any) =>
                        intfOrgEnpoList.findIndex((subItem: any) => item.orgEnpoId === subItem.orgEnpoId) === -1 &&
                        grid.intfOrgEnpoList.addRow(item),
                );
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "lg",
                content: <OSCI0113007Q callback={modalCallback} />,
            });
        },

        // Delete document target list deletion event !== 문서 대상 목록 삭제 이벤트 ==!
        onBtnGrdDelRowClick: () => {
            const seltList: any[] = grid.intfOrgEnpoList.getChecked() || [];
            if (comnUtils.isEmpty(seltList)) {
                modal.openModal({ content: "msg.osc.00009" });
                return;
            }

            modal.openModal({
                content: "msg.osc.00095",
                onConfirm: () => {
                    grid.intfOrgEnpoList.deleteRow("checkbox");
                },
            });
        },
    };

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Layout>
                        <Layout.Left>
                            <Group.Title title={"T_INTF_DOC_LIST"} titleSize={2}></Group.Title>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.intfDocMgmtInfo.schema.intfId} />
                            <Group.Control {...form.intfDocMgmtInfo.schema.intfNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocMgmtInfo.schema.sndrNm} />
                            <Group.Control {...form.intfDocMgmtInfo.schema.rcvrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocMgmtInfo.schema.sndrSysmNm} />
                            <Group.Control {...form.intfDocMgmtInfo.schema.rcvrSysmNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocMgmtInfo.schema.inexTpCd} />
                            <Group.Control {...form.intfDocMgmtInfo.schema.sndrcvDvnCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocMgmtInfo.schema.lnkgIntfId} />
                            <Group.Control {...form.intfDocMgmtInfo.schema.rspnIntfId} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Layout>
                        <Layout.Left>
                            <Group.Title title={"T_API_INFO"} titleSize={2}></Group.Title>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.intfDocMgmtInfo.schema.sndrcvTpCd} />
                            <Group.Control {...form.intfDocMgmtInfo.schema.lnkgPrtclCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocMgmtInfo.schema.lnkgDtaTpCd} />
                            <Group.Control {...form.intfDocMgmtInfo.schema.prcssMetdCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocMgmtInfo.schema.lnkgCycleCd} />
                            <Group.Control {...form.intfDocMgmtInfo.schema.rcveEnpoAddr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocMgmtInfo.schema.actvtYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocMgmtInfo.schema.inpInfoCn} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.intfDocMgmtInfo.schema.otptInfoCn} controlSize={10} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Layout>
                        <Layout.Left>
                            <Group.Title title={"T_ORG_ENPO_LST"} titleSize={2}></Group.Title>
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="gridAdd"
                                onClick={() => {
                                    handler.onBtnGrdAddRowClick();
                                }}
                            ></Button>
                            <Button
                                role="gridDelete"
                                onClick={() => {
                                    handler.onBtnGrdDelRowClick();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                    <Group.Section>
                        <Grid {...grid.intfOrgEnpoList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0113008q);
                        }}
                    />
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="save"
                        onClick={() => {
                            handler.onSaveBtnClick();
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
